import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Nav, Navbar, Dropdown, NavDropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome';
import { faPassport } from '@fortawesome/free-solid-svg-icons/faPassport';
import { faBuilding } from '@fortawesome/free-solid-svg-icons/faBuilding';
import { faUsersLine } from '@fortawesome/free-solid-svg-icons/faUsersLine';
import { faHouseCircleCheck } from '@fortawesome/free-solid-svg-icons/faHouseCircleCheck';

import { useUserSelector } from 'services/UserSelectorContext';

import SignOutButton from 'components/SignOutButton';
import { useUserRole } from '../../hooks/useUserRole';

const LanguageWrapper = styled.div`
  margin-top: -3px;
  margin-right: 4px;

  .dropdown-toggle::after {
    display: none !important;
  }
`;

const AdminLogo = styled.div`
  position: relative;
  right: -67px;
  top: -7px;
  font-size: 1rem;
`;

function Menu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, isLoggedIn, user } = useUserSelector();
  const { pathname } = useLocation();
  const { isSuperAdmin } = useUserRole();
  const flagImg = useMemo(() => {
    switch (i18n.language) {
      case 'es':
        return 'es.png';
      default:
        return 'en.png';
    }
  }, [i18n.language]);

  if (isLoading) {
    return null;
  }

  return (
    <Navbar bg="primary" variant="dark" expand="lg">
      <Container>
        <Navbar.Brand href="/">
          <img src="/logo.png" width="120px" alt="logo" />
          <AdminLogo>ADMIN</AdminLogo>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        {isLoggedIn ? (
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto justify-content-end">
              <Nav.Link onClick={() => navigate('/')} active={pathname === '/'}>
                <FontAwesomeIcon icon={faHome} /> {t('Home')}
              </Nav.Link>
              <NavDropdown
                title={
                  <>
                    <FontAwesomeIcon icon={faUsersLine} /> {t('Users')}
                  </>
                }
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item onClick={() => navigate('/users')}>Active users</NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/removed-users')}>
                  Removed users
                </NavDropdown.Item>

                <NavDropdown.Item onClick={() => navigate('/pipedrive-duplicates')}>
                  Pipedrive Duplicates
                </NavDropdown.Item>
              </NavDropdown>
              <NavDropdown
                title={
                  <>
                    <FontAwesomeIcon icon={faUsersLine} /> {t('Referrals')}
                  </>
                }
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item onClick={() => navigate('/referrals/earnings')}>
                  Referral Earnings
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/referrals/earnings/review-requests')}>
                  Referral Earnings Review Requests
                </NavDropdown.Item>
              </NavDropdown>
              <NavDropdown
                title={
                  <>
                    <FontAwesomeIcon icon={faPassport} /> {t('Passport')}
                  </>
                }
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item onClick={() => navigate('/passport-requests')}>
                  Passport requests
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/passport-onboardings')}>
                  Passport onboardings
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/passport/credilink')}>
                  Credilink Leads
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/smartfx-pending')}>
                  Pending Smartfx Requests
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/smartfx')}>
                  Submitted Smartfx Requests
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/smartfx-failed')}>
                  Failed Smartfx Requests
                </NavDropdown.Item>
              </NavDropdown>
              <NavDropdown
                title={
                  <>
                    <FontAwesomeIcon icon={faHouseCircleCheck} /> {t('Madrid Real Estate')}
                  </>
                }
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item onClick={() => navigate('/seller-cases')}>
                  Seller Cases
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/buyer-cases')}>
                  Buyer Cases
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/property-listings')}>
                  Property Listings
                </NavDropdown.Item>
              </NavDropdown>
              <NavDropdown
                title={
                  <>
                    <FontAwesomeIcon icon={faBuilding} /> {t('Partner companies')}
                  </>
                }
                id="basic-nav-dropdown"
              >
                <NavDropdown.Item onClick={() => navigate('/partner-companies')}>
                  Companies List
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => navigate('/partner-earnings')}>
                  Partner Earnings
                </NavDropdown.Item>
              </NavDropdown>

              {isSuperAdmin ? (
                <NavDropdown
                  title={
                    <>
                      <FontAwesomeIcon icon={faBuilding} /> {t('Settings')}
                    </>
                  }
                  id="basic-nav-dropdown"
                >
                  <NavDropdown.Item
                    onClick={() => navigate('/banks')}
                    active={pathname === '/banks'}
                  >
                    {t('Banks')}
                  </NavDropdown.Item>
                  <NavDropdown.Item onClick={() => navigate('/utm')} active={pathname === '/utm'}>
                    {t('UTM')}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    onClick={() => navigate('/property-finder/settings')}
                    active={pathname === '/property-finder/settings'}
                  >
                    {t('Property Finder')}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    onClick={() => navigate('/pipeline-settings')}
                    active={pathname.startsWith('/pipeline-settings')}
                  >
                    {t('Pipeline Settings')}
                  </NavDropdown.Item>
                </NavDropdown>
              ) : null}
            </Nav>

            <Navbar.Text className="d-flex">
              <LanguageWrapper>
                <Dropdown size="sm">
                  <Dropdown.Toggle split id="dropdown-basic">
                    <img src={`/${flagImg}`} alt="vector" width="22" role="button" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => i18n.changeLanguage('en')}>
                      <div>
                        <img src="/en.png" alt="vector" width="22" role="button" />
                        &nbsp;
                        <span className="text-black mt-1">English</span>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => i18n.changeLanguage('es')}>
                      <div>
                        <img src="/es.png" alt="vector" width="22" role="button" />
                        &nbsp;
                        <span className="text-black mt-1">Español</span>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </LanguageWrapper>

              <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                <i>{user.email}</i>&nbsp;
                <SignOutButton />
              </div>
            </Navbar.Text>
          </Navbar.Collapse>
        ) : null}
      </Container>
    </Navbar>
  );
}

export default Menu;
